import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

export default class extends Controller {
    static targets = ["menu", "caret", "input", "list", "item", "createRow", "createLabel", "newToggle", "newForm", "newInput"]

    static values = {
        createUrl: String,
        filter: String,
        currentId: String
    }

    connect() {
        this.outsideClick = (event) => {
            if (!this.element.contains(event.target)) this.close()
        }
        this.docKeydown = (event) => {
            if (event.key === "Escape") this.close()
        }
    }

    disconnect() {
        this.close()
    }

    toggle(event) {
        event.preventDefault()
        if (this.menuTarget.classList.contains("is-open")) {
            this.close()
        } else {
            this.open()
        }
    }

    open() {
        this.menuTarget.classList.add("is-open")
        this.caretTarget.setAttribute("aria-expanded", "true")
        document.addEventListener("click", this.outsideClick)
        document.addEventListener("keydown", this.docKeydown)
        if (this.hasInputTarget) {
            this.inputTarget.value = ""
            this.filter()
            this.inputTarget.focus()
        }
    }

    close() {
        if (this.hasMenuTarget) this.menuTarget.classList.remove("is-open")
        if (this.hasCaretTarget) this.caretTarget.setAttribute("aria-expanded", "false")
        document.removeEventListener("click", this.outsideClick)
        document.removeEventListener("keydown", this.docKeydown)
        this._collapseNew()
    }

    choose() {
        this.close()
    }

    filter() {
        let query = this._query()
        let anyVisible = false
        this.itemTargets.forEach((row) => {
            let match = query === "" || row.dataset.name.includes(query)
            row.hidden = !match
            if (match) anyVisible = true
        })
        this._updateCreateRow(query, anyVisible)
    }

    keydown(event) {
        if (event.key === "Enter") {
            event.preventDefault()
            if (this.hasCreateRowTarget && !this.createRowTarget.hidden) this.createFromFilter()
        } else if (event.key === "Escape" && this.inputTarget.value !== "") {
            event.preventDefault()
            event.stopPropagation()
            this.inputTarget.value = ""
            this.filter()
        }
    }

    createFromFilter() {
        let name = this._query({ raw: true })
        if (!name) return
        this._createList(name)
    }

    toggleNew(event) {
        event.preventDefault()
        if (!this.newFormTarget.hidden) {
            this._collapseNew()
            return
        }
        this.newFormTarget.hidden = false
        this.newToggleTarget.hidden = true
        this.newInputTarget.value = ""
        this.newInputTarget.focus()
    }

    newKeydown(event) {
        if (event.key === "Enter") {
            event.preventDefault()
            this.createFromNew()
        } else if (event.key === "Escape") {
            event.preventDefault()
            this._collapseNew()
        }
    }

    createFromNew() {
        let name = this.hasNewInputTarget ? this.newInputTarget.value.trim() : ""
        if (!name) return
        this._createList(name)
    }

    _createList(name) {
        if (!this.createUrlValue) return

        fetch(this.createUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ name, filter: this.filterValue || null })
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not create list")
                window.location.assign(data.switch_url)
            })
            .catch(() => {})
    }

    _updateCreateRow(query, anyVisible) {
        if (!this.hasCreateRowTarget) return
        let exact = this.itemTargets.some((row) => row.dataset.name === query)
        let show = query !== "" && !exact && this.createUrlValue
        this.createRowTarget.hidden = !show
        if (show && this.hasCreateLabelTarget) {
            this.createLabelTarget.textContent = `Create "${this._query({ raw: true })}"`
        }
        if (this.hasListTarget) {
            if (!show && !anyVisible && query !== "") this.listTarget.classList.add("is-empty")
            else this.listTarget.classList.remove("is-empty")
        }
    }

    _collapseNew() {
        if (this.hasNewFormTarget) this.newFormTarget.hidden = true
        if (this.hasNewToggleTarget) this.newToggleTarget.hidden = false
        if (this.hasNewInputTarget) this.newInputTarget.value = ""
    }

    _query({ raw = false } = {}) {
        let value = this.hasInputTarget ? this.inputTarget.value.trim() : ""
        return raw ? value : value.toLowerCase()
    }

}
